import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

import { api_key, sdkWrapperURL } from "../utils/api-url-list";
import { useAuth } from "../utils/auth";
import { logAnalyticsEvent } from "../utils/analytics";

// import UnityHealthNewLogo from "../assets/icons/unityhealthnewlogo.png";
import UnityHealthLogo from "../assets/images/unityhealth-logo.svg";
import HeartLogoUnity from "../assets/images/heart_logo_unity.png";
import CareplixLogo from "../assets/images/careplix-white-logo.png";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";

const allowedClientCodes = new Set(["CPXINT", "IFLIFE", "ALPHAMD", "ABHI"]);

const Login = () => {
  const { login } = useAuth();
  const [isModalOpen, setModalOpen] = useState(true);
  const [isLoading, setLoading] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");

  const [clientCode, setClientCode] = useState("");
  const [clientCodeError, setClientCodeError] = useState("");

  const [isOTPSent, setOTPSent] = useState(false);
  const otpTimerRef = useRef(null);
  const [otp, setOTP] = useState("");
  const [otpError, setOTPError] = useState("");
  const [otpTimer, setOTPTimer] = useState(0);

  const sendOTP = async () => {
    try {
      setLoading(true);
      setClientCodeError("");
      setPhoneNumberError("");

      if (!allowedClientCodes.has(clientCode)) {
        setClientCode(clientCode.trim());
        setClientCodeError("Enter a valid client code ");
        return;
      }

      if (!/^\d{10}$/.test(phoneNumber)) {
        setPhoneNumber(phoneNumber.trim());
        setPhoneNumberError("Please enter a valid phone number.");
        return;
      }

      const resp = await fetch(sdkWrapperURL("/auth/send-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key,
          action: "send",
          channel_id: "login_mobile",
          phone_no: phoneNumber,
        }),
      });

      const resp_json = await resp.json();

      if (resp_json.statusCode?.toString().startsWith("2")) {
        setOTPTimer(120);
        clearInterval(otpTimerRef.current);

        otpTimerRef.current = setInterval(
          () => setOTPTimer((t) => t - 1),
          1000
        );

        setOTP("");
        setOTPSent(true);

        logAnalyticsEvent("login_otp_requested", { phoneNumber });
      } else {
        throw new Error(resp_json.message);
      }
    } catch (err) {
      cancelOTP();
      setPhoneNumberError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    try {
      setLoading(true);
      setOTPError("");

      if (!/^\d{6}$/.test(otp)) {
        setOTP(otp.trim());
        setOTPError("Incorrect OTP provided. Please try again");
        return;
      }

      const resp = await fetch(sdkWrapperURL("/auth/sign-in"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key,
          login_type: "auth",
          login_method: "otp_mobile",
          phone_no: phoneNumber,
          otp,
        }),
      });

      const resp_json = await resp.json();

      if (resp_json.statusCode?.toString().startsWith("2")) {
        localStorage.setItem("client_code", clientCode.trim().toUpperCase());

        await login({
          id_token: resp_json.user.id_token,
          refresh_token: resp_json.user.refresh_token,
        });

        logAnalyticsEvent("login_otp_verified", { phoneNumber });
      } else {
        throw new Error(resp_json.message);
      }
    } catch (err) {
      setOTPError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelOTP = (goBack = true) => {
    setOTPTimer(0);
    clearInterval(otpTimerRef.current);

    setOTP("");
    setOTPError("");

    if (goBack) setOTPSent(false);
  };

  useEffect(() => {
    if (otpTimer <= 0) cancelOTP(false);
  }, [otpTimer]);

  return (
    <section className="min-h-screen p-8 relative bg-primary text-white tab-screen">
      {/* BG Glow */}
      <div className="absolute inset-0 z-0 flex justify-center pointer-events-none">
        <div className="w-[80%] h-64 mt-10 blur-[90px] rounded-full bg-[#A7D4FF]/60"></div>
      </div>

      {/* Intro Modal */}
      <Transition
        show={isModalOpen}
        appear
        leave="transition-opacity duration-500"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        as={Fragment}
      >
        <Dialog as={Fragment} onClose={() => {}}>
          <Dialog.Panel className="fixed inset-0 bg-primary flex flex-col justify-between">
            {/* Blur */}
            <div className="absolute inset-0 z-0 flex justify-center pointer-events-none">
              <div className="w-[60%] h-64 mt-1 blur-[90px] rounded-full bg-[#A7D4FF]/40"></div>
            </div>

            <div className="top-col flex justify-end flex-col items-center ">
              <img
                className="object-contain w-[150px] -mb-4 "
                src={HeartLogoUnity}
              />
              <img
                className="object-contain w-70 h-70 "
                src={UnityHealthLogo}
              />
              <p className="text-white text-xl font-ligh -mt-3">
                {" "}
                Digital Health Screening
              </p>
            </div>

            <div className="relative px-8 py-16 flex flex-col items-center">
              <button
                className="px-4 py-2 rounded-full bg-white text-primary text-lg w-10/12"
                onClick={() => {
                  logAnalyticsEvent("get_started");
                  setModalOpen(false);
                }}
              >
                <span
                  className="from-[#455C8E] via-[#455C8E] to-[#41a0e1] 
                  bg-gradient-to-r bg-clip-text text-transparent font-medium"
                >
                  Get Started
                </span>
              </button>

              <p className="text-white text-xs mt-8 font-light">A product by</p>
              <img src={CareplixLogo} />
            </div>
          </Dialog.Panel>
        </Dialog>
      </Transition>

      {/* Main Login */}
      {isOTPSent ? (
        <>
          <h1 className="text-2xl lg:text-4xl font-semibold text-center">
            Verify <span className="text-secondary">OTP</span>
          </h1>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              otpTimer > 0 ? verifyOTP() : sendOTP();
            }}
          >
            <input
              className="block w-full mt-6 px-4 py-2 rounded-lg border border-[#fff]/30 bg-transparent text-white"
              placeholder={otpTimer > 0 ? "Enter OTP" : "Timed Out"}
              type="text"
              maxLength={6}
              value={otp}
              onChange={(e) =>
                e.target.value.length <= 6 && setOTP(e.target.value)
              }
            />

            {otpError && (
              <p className="mt-1.5 text-xxs text-error">{otpError}</p>
            )}

            {otpTimer > 0 && (
              <p className="mt-1.5 text-sm text-right">
                Resend OTP in {Math.floor(otpTimer / 60)}:
                {(otpTimer % 60).toString().padStart(2, "0")}
              </p>
            )}

            <button
              className="w-full mt-6 px-5 py-2.5 rounded-full bg-white text-black flex items-center justify-center"
              disabled={isLoading}
            >
              <span>{otpTimer > 0 ? "Verify OTP" : "Resend OTP"}</span>
              {isLoading && <Loader className="h-4 w-4" />}
            </button>

            <button
              className="block mx-auto mt-6 text-sm font-medium"
              onClick={cancelOTP}
              type="button"
            >
              Cancel
            </button>
          </form>
        </>
      ) : (
        <>
          <h1 className="text-2xl lg:text-4xl font-semibold text-center">
            Get <span className="text-secondary">Started</span>
          </h1>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendOTP();
            }}
          >
            {/* Phone */}
            <input
              className="block w-full mt-6 px-4 py-2 rounded-lg border border-[#fff]/30 bg-transparent text-white"
              placeholder="Enter Phone Number"
              type="tel"
              maxLength={10}
              value={phoneNumber}
              onChange={(e) =>
                e.target.value.length <= 10 && setPhoneNumber(e.target.value)
              }
            />

            {phoneNumberError && (
              <p className="mt-1.5 text-xxs text-error">{phoneNumberError}</p>
            )}

            {/* Client Code (manual entry) */}
            <input
              className="block w-full mt-4 px-4 py-2 rounded-lg border border-[#fff]/30 bg-transparent text-white"
              placeholder="Enter Client Code"
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value.toUpperCase())}
            />

            {clientCodeError && (
              <p className="mt-1.5 text-xxs text-error">{clientCodeError}</p>
            )}

            <button
              className="w-full mt-6 px-5 py-2.5 rounded-full bg-white text-black flex items-center justify-center"
              disabled={isLoading}
            >
              <span>Send OTP</span>
              {isLoading && <Loader className="h-4 w-4" />}
            </button>
          </form>
        </>
      )}
    </section>
  );
};

export default Login;
